import { Injectable, computed, inject } from '@angular/core';
import { AuthSessionStore } from './auth-session.store';
import { FEATURE_PERMISSION_MODES, FEATURE_PERMISSIONS } from './feature-permissions';
import { HOME_CARDS, HOME_PATH, type HomeCard } from './home-cards';

export type PermissionMode = 'all' | 'any';

export interface PermissionRequirement {
  readonly permissions: readonly string[];
  readonly mode: PermissionMode;
}

/**
 * Ordered entry points for users who open the application without a return URL.
 *
 * Keep this list to real, identifier-free workspace routes. Detail screens such
 * as an admission, assessment, or note cannot be a safe landing because they
 * require a record identifier. The order preserves the established operational
 * priorities while ensuring every top-level navigation capability has a valid
 * destination instead of falling through to `/forbidden`.
 */
export const WORKSPACE_LANDING_CANDIDATES: ReadonlyArray<
  readonly [path: string, requirement: PermissionRequirement]
> = [
  ['/operations/dashboards', requirementFor('dashboards')],
  ['/patients', requirementFor('patients')],
  ['/bed-board', requirementFor('bedBoard')],
  ['/housekeeping', requirementFor('housekeeping')],
  ['/clinical-encounters', requirementFor('clinicalEncounters')],
  ['/nursing-escalations', requirementFor('nursingEscalations')],
  ['/operations/alerts', requirementFor('alertWorklist')],
  ['/laboratory/worklist', requirementFor('laboratoryWorklist')],
  ['/radiology/worklist', requirementFor('radiologyWorklist')],
  ['/diagnostics/critical-alerts', requirementFor('criticalAlerts')],
  ['/laboratory/tests', requirementFor('laboratoryCatalogue')],
  ['/radiology/services', requirementFor('radiologyCatalogue')],
  ['/pharmacy/medications', requirementFor('pharmacyCatalogue')],
  ['/pharmacy/orders', requirementFor('pharmacyOrders')],
  ['/pharmacy/dispenses', requirementFor('pharmacyDispenses')],
  ['/inventory/stock-count', requirementFor('inventoryStockCount')],
  ['/inventory/reorder-levels', requirementFor('inventoryReorderLevels')],
  ['/inventory/merge-items', requirementFor('inventoryMergeItems')],
  ['/inventory/sale-prices', requirementFor('inventorySalePrices')],
  ['/inventory/packaging', requirementFor('inventoryPackaging')],
  ['/inventory/ledger', requirementFor('inventoryLedger')],
  ['/inventory/approvals', requirementFor('inventoryApprovals')],
  ['/purchasing/invoices', requirementFor('purchaseInvoices')],
  ['/purchasing/orders', requirementFor('purchaseOrders')],
  ['/purchasing/receipts', requirementFor('goodsReceipts')],
  ['/appointments', requirementFor('appointments')],
  ['/patient-flow', requirementFor('patientFlow')],
  ['/printing', requirementFor('printing')],
  ['/finance', requirementFor('finance')],
  ['/finance/accommodation-rates', requirementFor('accommodationRates')],
  ['/finance/daily-cash', requirementFor('cashTills')],
  ['/finance/expenses', requirementFor('expenses')],
  ['/finance/partners', requirementFor('partners')],
  ['/finance/ownership', requirementFor('ownershipRegister')],
  ['/finance/accounting-periods', requirementFor('accountingPeriods')],
  ['/finance/management-fees', requirementFor('managementFeeRules')],
  ['/finance/profit-workflow', requirementFor('profitWorkflow')],
  ['/finance/profit-shares', requirementFor('profitShares')],
  ['/partner-portal', requirementFor('partnerPortal')],
  ['/reports/exports', requirementFor('reportExports')],
  ['/finance/dashboard', requirementFor('financeDashboard')],
  ['/administration/users', requirementFor('administrationUsers')],
  ['/administration/roles', requirementFor('administrationRoles')],
  ['/administration/organization', requirementFor('administrationOrganization')],
  ['/administration/settings', requirementFor('administrationSettings')],
  ['/administration/audit', requirementFor('administrationAudit')],
  ['/administration/audit-trail', requirementFor('administrationAuditScoped')],
  ['/administration/facilities', requirementFor('administrationFacilities')],
  ['/operations/inbox', requirementFor('notificationInbox')],
];

/**
 * The kind of work someone does, and where that work starts.
 *
 * The list above is one order for the whole hospital, so it answers "which of
 * these may this user open" and not "which of these is this user's job". Those
 * gave the same answer for nine of the thirteen seeded roles: a doctor, a
 * pharmacist, a radiologist, a laboratory technician, an accountant and a
 * receptionist all opened the application on `/operations/dashboards`, because
 * it is first in the list and they can all read a dashboard.
 *
 * A profile is matched on a *signature* -- a permission that marks the work
 * rather than merely permitting it. Placing an order is what a prescriber
 * does; reading one is what half the hospital does, so `orders.place` is a
 * signature and `orders.view` is not. The signature only chooses the ordering;
 * every destination is still checked against its own requirement from the list
 * above, so a profile can never route someone to a screen they may not open.
 *
 * Signatures are permissions, never role names. Roles are editable data -- an
 * administrator can rename `Nurse` or build a fourth doctor role this
 * afternoon -- and a landing rule that read those names would quietly stop
 * working the moment they did. It also means a role assembled by hand lands
 * correctly without anyone adding it here: what decides is the work it can do.
 *
 * Order matters, because a broad role satisfies several signatures. Super
 * Admin holds all 328 permissions and so matches every profile below; it is
 * caught by the first. `oversight` is deliberately first for that reason, and
 * `users.view` is its signature because a bedside doctor does not hold it
 * while a medical director and an administrator do.
 */
export interface WorkspaceProfile {
  readonly key: string;
  /** What marks this kind of work. Matched in `any` mode. */
  readonly signature: readonly string[];
  /**
   * Ordered destinations, most specific first. Every entry must appear in
   * `WORKSPACE_LANDING_CANDIDATES`, which supplies its permission
   * requirement; `landingProfilesAreRoutable` asserts that at build time.
   */
  readonly destinations: readonly string[];
}

export const WORKSPACE_PROFILES: readonly WorkspaceProfile[] = [
  // Supervisory and administrative. Lands where it does today, so neither a
  // system administrator nor a medical director changes destination.
  {
    key: 'oversight',
    signature: ['users.view'],
    destinations: ['/operations/dashboards', '/patients', '/clinical-encounters'],
  },
  // Prescribers. The encounter worklist is the doctor's own list of work,
  // where the dashboard is a view of everyone's.
  {
    key: 'prescriber',
    signature: ['orders.place'],
    destinations: ['/clinical-encounters', '/diagnostics/critical-alerts', '/patients'],
  },
  // Nursing. The bed board is the ward as the nurse holds it; escalations
  // come second because a worklist that is usually empty makes a poor home,
  // and the assessment, handover and care-plan screens are all detail routes
  // that need a record identifier and so cannot be a landing at all.
  {
    key: 'nursing',
    signature: ['nursing.assessments.manage'],
    destinations: ['/bed-board', '/nursing-escalations', '/patients'],
  },
  {
    key: 'pharmacy',
    signature: ['pharmacy.dispenses.prepare', 'pharmacy.orders.review'],
    destinations: ['/pharmacy/orders', '/pharmacy/dispenses', '/pharmacy/medications'],
  },
  {
    key: 'laboratory',
    signature: ['lab.results.enter'],
    destinations: ['/laboratory/worklist', '/laboratory/tests'],
  },
  {
    key: 'radiology',
    // `rad.studies.acquire` is here so the radiography technician, who
    // acquires studies but does not report them, lands on the same worklist
    // rather than falling through to a dashboard.
    signature: ['rad.reports.create', 'rad.studies.acquire'],
    destinations: ['/radiology/worklist', '/radiology/services'],
  },
  // Reception and admissions. This one also holds invoicing and till custody,
  // but the front desk starts at the patient, not at the money. Before the
  // housekeeper's: the reception marks a bed cleaned too (2026-10-27), and
  // still starts at the front desk.
  {
    key: 'frontDesk',
    signature: ['admissions.create'],
    destinations: ['/patients', '/bed-board', '/appointments'],
  },
  {
    key: 'housekeeping',
    signature: ['beds.housekeeping.complete'],
    destinations: ['/housekeeping'],
  },
  // Finance is signed on doing the work, not on seeing it: `expenses.view`
  // reaches the general manager and the auditor as well, and both belong
  // somewhere else.
  {
    key: 'finance',
    signature: ['expenses.create', 'billing.charge-candidates.capture'],
    destinations: ['/finance/dashboard', '/finance', '/finance/expenses'],
  },
  // After finance, deliberately. A finance manager holds `audit.view` too, so
  // an audit profile placed above this one would take them off their own
  // dashboard; placed below, it catches the auditor, who reads the hospital
  // but works none of it. There is no permission that separates the two
  // directly -- the only difference between an auditor and a finance manager
  // in the seeded data is clinical read access -- so the ordering is the
  // discriminator, and it is the reason this entry is here rather than higher.
  {
    key: 'audit',
    signature: ['audit.view'],
    // The scoped trail sits between the two on purpose. Before it existed, a
    // holder of `audit.view` alone fell through to the finance dashboard --
    // the auditor, whose whole job is reading the trail, landed on somebody
    // else's money. The organization-wide screen stays first for whoever holds
    // `audit.view-all`, and the dashboard stays last for whoever holds
    // neither route.
    destinations: ['/administration/audit', '/administration/audit-trail', '/finance/dashboard'],
  },
  {
    key: 'inventory',
    signature: ['inventory.transfers.create', 'inventory.adjustments.create'],
    destinations: ['/inventory/ledger', '/inventory/approvals'],
  },
];

/**
 * The kinds of work whose first page is «الرئيسية» (the owner, 2026-10-13),
 * for whoever has one of its cards: the desk, the money and whoever oversees
 * them -- and anyone whose work no profile names. A doctor, a nurse, the
 * laboratory, the pharmacy, the store and the auditor keep their own lists.
 */
export const HOME_FIRST_PROFILES: ReadonlySet<string> = new Set(['oversight', 'frontDesk', 'finance']);

/**
 * Where a nurse and the nursing supervisor may go from the menu (the owner,
 * 2026-10-18: «عايز التمريض يظهرله الرئيسية بس», and asked, the nursing
 * work too, and for the supervisor the nurses' attendance and pay). Their
 * grants are untouched, so a screen opened from inside a patient still
 * opens; only the menu is short. «الرئيسية» comes first and is where they
 * land: they write themselves in and out there.
 */
export const NURSING_DESK_PATHS: ReadonlySet<string> = new Set([
  '/bed-board', '/patients', '/nursing-escalations', '/diagnostics/critical-alerts',
  '/operations/alerts', '/operations/inbox', '/me/attendance', '/staff/attendance-log', '/staff/payroll',
]);

const LANDING_REQUIREMENTS: ReadonlyMap<string, PermissionRequirement> = new Map(
  WORKSPACE_LANDING_CANDIDATES,
);

/**
 * Every profile destination resolves to a known candidate.
 *
 * Exported so the unit test can assert it rather than trusting a reading. A
 * path that is merely misspelled would otherwise fail silently -- the profile
 * would match, every destination would be skipped as unreachable, and the user
 * would fall through to the global list, landing exactly where they land
 * today. The bug would look like the feature simply not working.
 */
export function landingProfilesAreRoutable(): readonly string[] {
  return WORKSPACE_PROFILES.flatMap((profile) =>
    profile.destinations.filter((path) => !LANDING_REQUIREMENTS.has(path)),
  );
}

function requirementFor(
  feature: keyof typeof FEATURE_PERMISSIONS,
): PermissionRequirement {
  return {
    permissions: FEATURE_PERMISSIONS[feature],
    mode: FEATURE_PERMISSION_MODES[feature],
  };
}

function isPermissionKey(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.trim() === value;
}

function isPermissionList(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.length > 0 && value.every(isPermissionKey);
}

@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly session = inject(AuthSessionStore);
  private readonly permissionSet = computed(
    () => new Set(this.session.user()?.permissions ?? []),
  );

  readonly permissions = computed(() => [...this.permissionSet()].sort());

  /** The cards of «الرئيسية» this user may open (2026-10-13). */
  readonly homeCards = computed<readonly HomeCard[]>(() => HOME_CARDS.filter((card) => this.allows(card.requirement)));

  /**
   * A nurse or the nursing supervisor (2026-10-18): nursing is their work,
   * and they write themselves in and out. Their menu is short and their
   * first page is «الرئيسية».
   */
  readonly nursingDesk = computed(() => {
    this.permissionSet();
    const profile = WORKSPACE_PROFILES.find((candidate) => this.hasAny(candidate.signature));

    return profile?.key === 'nursing' && this.has('staff.self');
  });

  /**
   * A nurse, not the supervisor (2026-10-23: «يظهرله الرئيسية بس … مش لازم
   * يظهرله كل التفاصيل»): «الرئيسية» is all her menu holds.
   */
  readonly nurseDesk = computed(() => this.nursingDesk() && !this.has('staff.nursing.view'));

  /**
   * A doctor with an account tied to his name (2026-10-20: «حضور وانصراف
   * الاطباء زي التمريض»): he writes himself in and out, and his first page is
   * «الرئيسية»; his menu stays as it is.
   */
  readonly doctorDesk = computed(() => {
    this.permissionSet();
    const profile = WORKSPACE_PROFILES.find((candidate) => this.hasAny(candidate.signature));

    return profile?.key === 'prescriber' && this.has('staff.self');
  });

  /** Whether «الرئيسية» has anything for this user: one of its cards, or their own check-in. */
  readonly homeOpen = computed(() => this.nursingDesk() || this.doctorDesk() || this.homeCards().length > 0);

  has(permission: string): boolean {
    return isPermissionKey(permission) && this.permissionSet().has(permission);
  }

  hasAny(permissions: readonly string[]): boolean {
    return isPermissionList(permissions) && permissions.some((permission) => this.has(permission));
  }

  hasAll(permissions: readonly string[]): boolean {
    return isPermissionList(permissions) && permissions.every((permission) => this.has(permission));
  }

  allows(requirement: PermissionRequirement | null | undefined): boolean {
    if (!requirement || typeof requirement !== 'object') {
      return false;
    }

    if (!isPermissionList(requirement.permissions)) {
      return false;
    }

    if (requirement.mode === 'all') {
      return this.hasAll(requirement.permissions);
    }

    return requirement.mode === 'any' && this.hasAny(requirement.permissions);
  }

  /**
   * Where this user's work starts.
   *
   * The profile decides the ordering; the requirement decides admission. A
   * profile whose destinations are all unreachable falls through to the global
   * list rather than to `/forbidden`, so a partially-granted account still
   * lands somewhere it may be.
   */
  landingRoute(): string {
    const profile = WORKSPACE_PROFILES.find((candidate) => this.hasAny(candidate.signature));
    if (this.nursingDesk() || this.doctorDesk()) {
      return HOME_PATH;
    }
    if ((profile === undefined || HOME_FIRST_PROFILES.has(profile.key)) && this.homeCards().length > 0) {
      return HOME_PATH;
    }

    const preferred = profile?.destinations
      .find((path) => this.allows(LANDING_REQUIREMENTS.get(path)));

    return preferred
      ?? WORKSPACE_LANDING_CANDIDATES
        .find(([, requirement]) => this.allows(requirement))?.[0]
      ?? '/forbidden';
  }
}
